import { useContext, useState } from 'react'

import CancelWarningModal from '@ors/components/manage/Blocks/ProjectsListing/ProjectSubmission/CancelWarningModal'
import Link from '@ors/components/ui/Link/Link'
import { useUpdatedFields } from '@ors/contexts/Projects/UpdatedFieldsContext'
import PermissionsContext from '@ors/contexts/PermissionsContext'
import { matchPath } from '@ors/helpers/Url/Url'

import { IoChevronDown, IoChevronUp, IoClose, IoMenu } from 'react-icons/io5'
import { DebouncedFunc, debounce } from 'lodash'
import { useLocation } from 'wouter'
import {
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
} from '@mui/material'
import cx from 'classnames'

const EXTERNAL_BASE_URL = 'https://www.multilateralfund.org'
const makeExternalUrl = (path: string) => `${EXTERNAL_BASE_URL}${path}`

interface MenuItem {
  current?: boolean
  label: string
  menu?: MenuItem[]
}

const makeInternalNavItem = (
  pathname: string,
  props: {
    label: string
    menu?: { label: string; url: string; external?: boolean }[]
    url: string
  },
) => {
  const current = props.menu
    ? props.menu.some((menuItem) =>
        matchPath(`${menuItem.url}/*`, pathname || ''),
      )
    : matchPath(`${props.url}/*`, pathname || '') !== null

  const internal = true

  const processedMenu = props.menu?.map((menuItem) => ({
    ...menuItem,
    current: !!matchPath(`${menuItem.url}/*`, pathname || ''),
    internal: !menuItem.external,
  }))

  return {
    current,
    internal,
    ...props,
    menu: processedMenu,
  }
}

const useInternalNavSections = () => {
  const P = useContext(PermissionsContext)

  const [pathname] = useLocation()
  const nI = makeInternalNavItem.bind(null, pathname)

  if (!(P.canViewCPReports || P.canEditCPReports || P.canExportCPReports)) {
    return []
  }

  return [
    {
      label: 'Online CP Reporting',
      menu: [
        ...(P.canViewCPReports
          ? [{ label: 'View reports', url: '/country-programme/reports' }]
          : []),
        ...(P.canEditCPReports
          ? [{ label: 'Add new report', url: '/country-programme/create' }]
          : []),
        ...(P.canExportCPReports
          ? [{ label: 'Export data', url: '/country-programme/export-data' }]
          : []),
        ...(P.canExportCPReports
          ? [{ label: 'Settings', url: '/country-programme/settings' }]
          : []),
      ].filter(Boolean),
      url: '/country-programme/reports',
    },
    // @ts-ignore
  ].map((item) => nI(item))
}

const useInternalNavSectionsReplenishment = () => {
  const { canViewReplenishment } = useContext(PermissionsContext)
  const [pathname] = useLocation()
  const nI = makeInternalNavItem.bind(null, pathname)

  if (!canViewReplenishment) {
    return []
  }

  return (
    [
      {
        label: 'Scale of assessment',
        url: '/replenishment/scale-of-assessment',
      },
      {
        label: 'Status of the fund',
        url: '/replenishment/status-of-the-fund',
      },
      { label: 'Statistics', url: '/replenishment/statistics' },
      {
        label: 'Status of contributions',
        url: '/replenishment/status-of-contributions',
      },
      { label: 'In/out flows', url: '/replenishment/in-out-flows' },
      { label: 'Dashboard', url: '/replenishment/dashboard' },
    ]
      // @ts-ignore
      .map((item) => nI(item))
  )
}

const useInternalNavSectionsIaBaPortal = () => {
  const { canViewBp, canViewProjects, canSetProjectSettings } =
    useContext(PermissionsContext)
  const [pathname] = useLocation()
  const nI = makeInternalNavItem.bind(null, pathname)

  if (!(canViewBp || canViewProjects || canSetProjectSettings)) {
    return []
  }

  return (
    [
      {
        label: 'IA/BA Portal',
        menu: [
          ...(canViewBp
            ? [{ label: 'Business plans', url: '/business-plans' }]
            : []),
          ...(canViewProjects
            ? [
                {
                  label: 'Projects Listing',
                  url: '/projects-listing/listing',
                },
              ]
            : []),
          ...(canSetProjectSettings
            ? [
                {
                  label: 'IA/BA portal settings',
                  url: '/projects-listing/settings',
                },
              ]
            : []),
          {
            label: 'CNTRB internal dashboard',
            url: 'https://app.powerbi.com/groups/4a9130ea-fe2e-4538-845a-65af312b9997/reports/8f88d02f-e3d0-4cc4-8deb-ceab660d22b3/b4d219601eca808cae7d?experience=power-bi',
            external: true,
          },
        ],
        url: '/projects-listing/listing',
      },
    ]
      // @ts-ignore
      .map((item) => nI(item))
  )
}

interface navItem {
  current?: boolean
  external?: boolean
  internal?: boolean
  label: string
  menu?: navItem[]
  url: string
}

const makeExternalNavItem = (label: string, url: string, menu?: navItem[]) => {
  return {
    current: menu ? !!menu.filter((item) => item.current).length : false,
    external: true,
    label,
    menu,
    url: makeExternalUrl(url),
  }
}

const useMenuItems = () => {
  return [
    makeExternalNavItem('About MLF', '/about', [
      makeExternalNavItem('Mission', '/about/mission'),
      makeExternalNavItem('History', '/about/history'),
      makeExternalNavItem('Governance', '/about/governance'),
      makeExternalNavItem('Partners', '/about/partners'),
      makeExternalNavItem('Secretariat', '/about/secretariat'),
      makeExternalNavItem('Evaluation', '/about/evaluation'),
      makeExternalNavItem(
        'Greening our operations',
        '/greening-our-operations',
      ),
    ]),
    makeExternalNavItem('Our impact', '/our-impact'),
    makeExternalNavItem('Our work', '/our-work', [
      makeExternalNavItem(
        'Project performance',
        '/our-work/project-performance',
      ),
      makeExternalNavItem('Project portfolio', '/our-work/project-portfolio'),
      makeExternalNavItem('Project analytics', '/our-work/project-analytics'),
      makeExternalNavItem('Country profiles', '/country-profiles'),
      makeExternalNavItem('CP Data Center', '/our-work/cp-data-center'),
      ...useInternalNavSections(),
      makeExternalNavItem(
        'Contributions',
        '/our-work/contributions',
        useInternalNavSectionsReplenishment(),
      ),
      makeExternalNavItem('Portals', '/our-work/portals'),
      ...useInternalNavSectionsIaBaPortal(),
    ]),
    makeExternalNavItem('Resources', '/resources', [
      makeExternalNavItem('Decisions', '/resources/decisions'),
      makeExternalNavItem(
        'Project Guides & Tools',
        '/resources/project-guides-tools',
      ),
      makeExternalNavItem('Scorecards', '/resources/secretariat-scorecards'),
      makeExternalNavItem('Factsheets', '/factsheets'),
      makeExternalNavItem('Newsletters', '/newsletters'),
      makeExternalNavItem('Policy frameworks', '/policy-frameworks'),
    ]),
    makeExternalNavItem('Meetings', '/meetings'),
    makeExternalNavItem('News & Stories', '/news-stories'),
  ]
}

const DesktopHeaderNavigation = ({
  className = '',
  items = [],
}: {
  className: string
  items: ReturnType<typeof useMenuItems>
}) => {
  const recursivelyExpandItems = (
    items: MenuItem[],
    acc: Record<string, boolean>,
  ) => {
    items.forEach((item) => {
      if (item.current) {
        acc[item.label] = true
      }
      if (item.menu) {
        recursivelyExpandItems(item.menu, acc)
      }
    })
  }

  const initiallyExpanded: Record<string, boolean> = {}
  recursivelyExpandItems(items, initiallyExpanded)

  const [location, setLocation] = useLocation()
  const { updatedFields } = useUpdatedFields()

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [crtUrl, setCrtUrl] = useState<string | null>(null)
  const [showMenu, setShowMenu] = useState<Record<string, boolean>>({})
  const [hideInProgress, setHideInProgress] = useState<
    Record<string, DebouncedFunc<any> | null>
  >({})
  const [openMenus, setOpenMenus] =
    useState<Record<string, boolean>>(initiallyExpanded)
  const toggleCollapseOpen = (label: string) => {
    setOpenMenus((prev) => {
      const newState = { [label]: !prev[label] }

      Object.keys(prev).forEach((key) => {
        if (key !== label) {
          newState[key] = false
        }
      })

      return newState
    })
  }

  const handleShowMenu = (label: string) => {
    hideInProgress[label]?.cancel?.()
    setShowMenu(() => ({ [label]: true }))
  }

  const handleToggleMenu = (label: string) => {
    hideInProgress[label]?.cancel?.()
    const prev = showMenu[label]
    setShowMenu(() => ({ [label]: !prev }))
  }

  const handleHideAllMenus = () => {
    Object.keys(showMenu).forEach((key) => {
      const eta = debounce(
        () => setShowMenu((prev) => ({ ...prev, [key]: false })),
        300,
      )
      setHideInProgress((prev) => ({ ...prev, [key]: eta }))
      eta()
    })
  }

  return (
    <div
      id="header-navigation"
      className={cx(
        'relative gap-x-4 text-nowrap rounded-full bg-white px-5 py-3 text-xl font-normal uppercase print:hidden',
        className,
      )}
      onMouseLeave={handleHideAllMenus}
    >
      {items.map((item) => (
        <div key={item.label} className="relative" data-label={item.label}>
          <div className="flex cursor-pointer text-primary">
            <div
              className={cx(
                'flex items-center justify-between gap-x-2 rounded-full px-4 py-2 text-primary transition-all hover:text-mlfs-hlYellow',
                {
                  'bg-mlfs-hlYellow hover:text-primary': item.current,
                  'hover:bg-primary': !item.current,
                },
              )}
              onMouseEnter={() => handleShowMenu(item.label)}
            >
              <Link className="text-inherit no-underline" href={item.url}>
                {item.label}
              </Link>
              {item.menu && (
                <div onClick={() => handleToggleMenu(item.label)}>
                  <IoChevronDown />
                </div>
              )}
            </div>
          </div>
          <div
            className={cx(
              'absolute left-0 z-50 mt-4 origin-top opacity-0 transition-all',
              {
                'collapse scale-y-0': !showMenu[item.label],
                'scale-y-100 opacity-100': showMenu[item.label],
              },
            )}
          >
            <div
              className="z-10 flex flex-col rounded-lg bg-white shadow-xl"
              onMouseLeave={handleHideAllMenus}
              onMouseOver={() => handleShowMenu(item.label)}
            >
              {item.menu?.map((menuItem, menuItemIdx) => {
                const Component = menuItem?.internal ? Link : 'a'
                const regularSubMenuLink =
                  !menuItem.menu || menuItem.menu.length === 0 ? (
                    <Component
                      key={menuItem.label + menuItemIdx}
                      className={cx(
                        'flex flex-nowrap items-center gap-1 text-nowrap border-2 border-l-0 border-r-0 border-t-0 border-solid border-b-sky-400 px-4 py-2 text-primary no-underline transition-all first:rounded-t-lg last:rounded-b-lg last:border-b-0 hover:bg-mlfs-hlYellow',
                        {
                          'bg-mlfs-hlYellow': menuItem.current,
                        },
                      )}
                      href={menuItem.url}
                      onClick={() => toggleCollapseOpen(menuItem.label)}
                    >
                      {menuItem.label}
                    </Component>
                  ) : null
                const isExternalWithMenu =
                  menuItem.external && menuItem.menu && menuItem.menu.length > 0

                return (
                  regularSubMenuLink || (
                    <List
                      key={menuItem.label + menuItemIdx}
                      className="py-0"
                      component="div"
                    >
                      <ListItemButton
                        className={cx(
                          'flex flex-nowrap items-center gap-1 text-nowrap rounded-b-none border-2 border-l-0 border-r-0 border-t-0 border-solid border-b-sky-400 px-4 py-2 text-primary no-underline transition-all first:rounded-t-lg hover:bg-mlfs-hlYellow',
                          {
                            'bg-mlfs-hlYellow':
                              menuItem.current && !menuItem.menu,
                          },
                        )}
                        {...(isExternalWithMenu ? { href: menuItem.url } : {})}
                        onClick={() => {
                          if (!isExternalWithMenu) {
                            toggleCollapseOpen(menuItem.label)
                          }
                        }}
                      >
                        {menuItem.label}
                        {openMenus[menuItem.label] ? (
                          <IoChevronUp
                            onClick={(e) => {
                              if (isExternalWithMenu) {
                                e.preventDefault()
                                toggleCollapseOpen(menuItem.label)
                              }
                            }}
                          />
                        ) : (
                          <IoChevronDown
                            onClick={(e) => {
                              if (isExternalWithMenu) {
                                e.preventDefault()
                                toggleCollapseOpen(menuItem.label)
                              }
                            }}
                          />
                        )}
                      </ListItemButton>
                      <Collapse
                        in={openMenus[menuItem.label]}
                        timeout="auto"
                        unmountOnExit
                      >
                        <List className="py-0" component="div">
                          {menuItem.menu &&
                            menuItem.menu.map((subMenuItem) => {
                              const isInternal = subMenuItem?.internal
                              const Component = isInternal ? Link : 'a'
                              const hasUnsavedChanges =
                                isInternal && updatedFields.size > 0

                              return (
                                <div key={subMenuItem.label}>
                                  <ListItem
                                    className={cx(
                                      'last:rounded-0 cursor-pointer text-nowrap border-2 border-l-0 border-r-0 border-t-0 border-solid border-b-sky-400 px-4 py-2 pl-8 text-lg text-primary no-underline transition-all hover:bg-mlfs-hlYellow',
                                      {
                                        'bg-mlfs-hlYellow': subMenuItem.current,
                                      },
                                    )}
                                    component={Component}
                                    href={
                                      !hasUnsavedChanges
                                        ? subMenuItem.url
                                        : null
                                    }
                                    onClick={() => {
                                      if (hasUnsavedChanges) {
                                        setIsCancelModalOpen(true)
                                        setCrtUrl(subMenuItem.url)
                                      } else if (subMenuItem.internal) {
                                        setLocation(subMenuItem.url)
                                      }
                                    }}
                                  >
                                    {subMenuItem.label}
                                  </ListItem>
                                  {isCancelModalOpen && !!crtUrl && (
                                    <CancelWarningModal
                                      mode="current action"
                                      url={crtUrl}
                                      isModalOpen={isCancelModalOpen}
                                      setIsModalOpen={setIsCancelModalOpen}
                                    />
                                  )}
                                </div>
                              )
                            })}
                        </List>
                      </Collapse>
                    </List>
                  )
                )
              })}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

const MobileHeaderNavigation = ({
  className = '',
  items = [],
}: {
  className: string
  items: ReturnType<typeof useMenuItems>
}) => {
  const initiallyExpanded: Record<string, boolean> = items.reduce(
    (acc, item) => {
      if (item.menu && item.menu.some((subItem) => subItem.current)) {
        acc[item.label] = true
      }
      if (item.menu) {
        item.menu.forEach((subItem) => {
          if (
            subItem.menu &&
            subItem.menu.some((subSubItem) => subSubItem.current)
          ) {
            acc[subItem.label] = true
          }
        })
      }
      return acc
    },
    {} as Record<string, boolean>,
  )

  const [location, setLocation] = useLocation()
  const { updatedFields } = useUpdatedFields()

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [crtUrl, setCrtUrl] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [openMenus, setOpenMenus] =
    useState<Record<string, boolean>>(initiallyExpanded)

  const toggleOpenMenu = (label: string) => {
    setOpenMenus((prev) => ({ ...prev, [label]: !prev[label] }))
  }

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen)
  }

  return (
    <div className={cx('text-primary print:hidden', className)}>
      <IconButton
        className="mobile-header-navigation-button rounded-full"
        onClick={toggleDrawer(true)}
      >
        <IoMenu size={42} />
      </IconButton>
      <Drawer
        className={cx('radius-none')}
        anchor="right"
        open={open}
        onClose={toggleDrawer(false)}
      >
        <div className="flex justify-end">
          <IconButton
            className="mobile-header-navigation-button m-6 rounded-full"
            onClick={toggleDrawer(false)}
          >
            <IoClose size={42} />
          </IconButton>
        </div>
        <List className="min-w-96 px-4">
          {items.map((item) => {
            const styling =
              'block border-2 border-l-0 border-r-0 border-t-0 border-solid border-b-primary px-6 py-4 text-xl uppercase text-primary no-underline transition-all hover:bg-mlfs-hlYellowTint'
            const regularMenuLink = !item.menu ? (
              <ListItem
                key={item.label}
                className={styling}
                component={'a'}
                href={item.url}
              >
                {item.label}
              </ListItem>
            ) : null
            return (
              regularMenuLink || (
                <div key={item.label}>
                  <ListItemButton
                    className={cx(
                      'flex items-center justify-between rounded-none',
                      styling,
                    )}
                    onClick={() => toggleOpenMenu(item.label)}
                  >
                    {item.label}
                    {openMenus[item.label] ? (
                      <IoChevronUp />
                    ) : (
                      <IoChevronDown />
                    )}
                  </ListItemButton>
                  <Collapse
                    in={openMenus[item.label]}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List component="div">
                      {item.menu &&
                        item.menu.map((menuItem) => {
                          const regularSubMenuLink =
                            !menuItem.menu || menuItem.menu.length === 0 ? (
                              <ListItem
                                key={menuItem.label}
                                className={cx(styling, 'pl-10')}
                                component={'a'}
                                href={menuItem.url}
                              >
                                {menuItem.label}
                              </ListItem>
                            ) : null
                          const isExternalWithMenu =
                            menuItem.external &&
                            menuItem.menu &&
                            menuItem.menu.length > 0

                          return (
                            regularSubMenuLink || (
                              <div key={menuItem.label}>
                                <ListItemButton
                                  className={cx(
                                    'flex items-center justify-between rounded-none pl-10',
                                    styling,
                                  )}
                                  {...(isExternalWithMenu
                                    ? { href: menuItem.url }
                                    : {})}
                                  onClick={() => {
                                    if (!isExternalWithMenu) {
                                      toggleOpenMenu(menuItem.label)
                                    }
                                  }}
                                >
                                  {menuItem.label}
                                  {openMenus[menuItem.label] ? (
                                    <IoChevronUp
                                      onClick={(e) => {
                                        if (isExternalWithMenu) {
                                          e.preventDefault()
                                          toggleOpenMenu(menuItem.label)
                                        }
                                      }}
                                    />
                                  ) : (
                                    <IoChevronDown
                                      onClick={(e) => {
                                        if (isExternalWithMenu) {
                                          e.preventDefault()
                                          toggleOpenMenu(menuItem.label)
                                        }
                                      }}
                                    />
                                  )}
                                </ListItemButton>
                                <Collapse
                                  in={openMenus[menuItem.label]}
                                  timeout="auto"
                                  unmountOnExit
                                >
                                  <List component="div">
                                    {menuItem.menu &&
                                      menuItem.menu.map((subMenuItem) => {
                                        const isInternal = subMenuItem?.internal
                                        const Component = isInternal
                                          ? Link
                                          : 'a'
                                        const hasUnsavedChanges =
                                          isInternal && updatedFields.size > 0

                                        return (
                                          <div key={subMenuItem.label}>
                                            <ListItem
                                              className={cx(
                                                'block cursor-pointer py-4 pl-12 text-xl uppercase text-primary no-underline transition-all hover:bg-mlfs-hlYellowTint',
                                                {
                                                  'bg-mlfs-hlYellowTint':
                                                    subMenuItem.current,
                                                },
                                              )}
                                              component={Component}
                                              href={
                                                !hasUnsavedChanges
                                                  ? subMenuItem.url
                                                  : null
                                              }
                                              onClick={() => {
                                                if (hasUnsavedChanges) {
                                                  setIsCancelModalOpen(true)
                                                  setCrtUrl(subMenuItem.url)
                                                } else if (
                                                  subMenuItem.internal
                                                ) {
                                                  setLocation(subMenuItem.url)
                                                }
                                              }}
                                            >
                                              {subMenuItem.label}
                                            </ListItem>
                                            {isCancelModalOpen && !!crtUrl && (
                                              <CancelWarningModal
                                                mode="current action"
                                                url={crtUrl}
                                                isModalOpen={isCancelModalOpen}
                                                setIsModalOpen={
                                                  setIsCancelModalOpen
                                                }
                                              />
                                            )}
                                          </div>
                                        )
                                      })}
                                  </List>
                                </Collapse>
                              </div>
                            )
                          )
                        })}
                    </List>
                  </Collapse>
                </div>
              )
            )
          })}
        </List>
      </Drawer>
    </div>
  )
}

const HeaderNavigation = () => {
  const items = useMenuItems()
  return (
    <>
      <DesktopHeaderNavigation className="hidden lg:flex" items={items} />
      <MobileHeaderNavigation className="lg:hidden" items={items} />
    </>
  )
}

export default HeaderNavigation
